import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { apiClient } from '@/utils/api'
import {
  User, Settings, CreditCard, LogOut, ChevronDown,
  Shield, Bell, HelpCircle
} from 'lucide-react'

interface SubscriptionInfo {
  status: string
  plan_name?: string
  trial_ends_at?: string
  // Phoenix backend returns these fields
  is_active?: boolean
  is_trial_period?: boolean
  trial_days_remaining?: number
  expiration_date?: string
  source?: string
}

export const AccountMenu: React.FC = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const data = await apiClient.get('/api/subscription/status')
        // Normalize Phoenix response to expected format
        const status = data.status || (
          data.is_trial_period ? 'trialing' :
          data.is_active ? 'active' :
          data.source === 'none' ? 'expired' : 'active'
        )
        const planName = data.plan_name || (
          data.is_trial_period ? 'Free Trial' :
          data.source === 'admin' ? 'Premium' :
          data.source === 'app_store' ? 'Family' :
          data.source === 'stripe' ? 'Family' :
          null
        )
        setSubscription({
          ...data,
          status,
          plan_name: planName,
          trial_ends_at: data.trial_ends_at || data.expiration_date
        })
      } catch (error) {
        // Subscription info not available
      }
    }
    fetchSubscription()
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = () => {
    sessionStorage.removeItem('oauth_token_processed')
    logout()
    navigate('/login')
  }

  const getSubscriptionBadge = () => {
    if (!subscription) return null

    const statusColors: Record<string, string> = {
      'active': 'bg-green-100 text-green-700',
      'trialing': 'bg-blue-100 text-blue-700',
      'trial': 'bg-blue-100 text-blue-700',
      'past_due': 'bg-amber-100 text-amber-700',
      'canceled': 'bg-gray-100 text-gray-700',
      'expired': 'bg-red-100 text-red-700'
    }

    const statusLabels: Record<string, string> = {
      'active': 'Active',
      'trialing': 'Trial',
      'trial': 'Trial',
      'past_due': 'Past Due',
      'canceled': 'Canceled',
      'expired': 'Expired'
    }

    const colorClass = statusColors[subscription.status] || 'bg-gray-100 text-gray-700'
    const label = statusLabels[subscription.status] || subscription.status

    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colorClass}`}>
        {label}
      </span>
    )
  }

  const getInitials = (name?: string) => {
    if (!name) return 'U'
    // Filter out words starting with non-letters (like parentheses)
    return name.split(' ')
      .filter(n => n.length > 0 && /^[a-zA-Z]/.test(n))
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || name.charAt(0).toUpperCase()
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
          {getInitials(user?.display_name)}
        </div>
        <div className="hidden sm:block text-left">
          <div className="text-sm font-medium text-gray-900">{user?.display_name}</div>
          <div className="text-xs text-gray-500">Parent</div>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium">
                {getInitials(user?.display_name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">{user?.display_name}</div>
                <div className="text-sm text-gray-500 truncate">{user?.email || 'Parent Account'}</div>
              </div>
            </div>
          </div>

          {/* Subscription Status */}
          {subscription && (
            <div className="px-4 py-3 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {subscription.plan_name || 'Subscription'}
                  </span>
                </div>
                {getSubscriptionBadge()}
              </div>
              {subscription.status === 'trialing' && subscription.trial_ends_at && (
                <div className="mt-1 text-xs text-gray-500">
                  Trial ends {new Date(subscription.trial_ends_at).toLocaleDateString()}
                </div>
              )}
            </div>
          )}

          {/* Menu Items */}
          <div className="py-1">
            <button
              onClick={() => {
                setIsOpen(false)
                navigate('/dashboard/settings')
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Settings className="w-4 h-4 text-gray-400" />
              Settings
            </button>

            <button
              onClick={() => {
                setIsOpen(false)
                navigate('/dashboard/sessions')
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Shield className="w-4 h-4 text-gray-400" />
              Active Sessions
            </button>

            {subscription && subscription.source !== 'admin' && subscription.source !== 'trial' && (
              <button
                onClick={() => {
                  setIsOpen(false)
                  navigate('/dashboard/settings')
                  // Navigate to subscription section
                  setTimeout(() => {
                    const event = new CustomEvent('navigate-to-settings-section', { detail: 'system-subscription' })
                    window.dispatchEvent(event)
                  }, 100)
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <CreditCard className="w-4 h-4 text-gray-400" />
                Manage Subscription
              </button>
            )}

            <button
              onClick={() => {
                setIsOpen(false)
                window.open('https://housepoints.ai/support', '_blank')
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <HelpCircle className="w-4 h-4 text-gray-400" />
              Help & Support
            </button>
          </div>

          {/* Logout */}
          <div className="border-t border-gray-100 pt-1">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
